import { Routes } from "@angular/router";
import { authGuard } from "@shared/guard/auth.guard";
import { BloggerGuard } from "@shared/guard/blogger.guard";
import { BehkhaanRoutes } from "@shared/utils/router-paths";
import { BooklistDetailResolver } from "./booklists/resolver/detail-booklist.resolver";
import { QuoteDetailResolver } from "./quotes/resolvers/detail-quote.resolver";
import { PostDetailResolver } from "./posts/resolvers/detail-post.resolver";
import { ReviewDetailResolver } from "./reviews/resolvers/detail-review.resolver";
import { BookDetailResolver } from "./books/resolvers/book-detail.resolver";
import { clubAdminGuard } from "@app/pages/clubs/guards/club-admin.guard";
import { clubMemberGuard } from "@app/pages/clubs/guards/club-member.guard";
import { ClubOwnerGuard } from "@shared/guard/club-owner.guard";
import { ClubsResolver } from "@app/pages/clubs/resolvers/clubs.resolver";
import { ClubDetailResolver } from "@app/pages/clubs/resolvers/club-detail.resolver";
import { ClubIntroResolver } from "@app/pages/clubs/resolvers/club-intro.resolver";
import { CourseDetailResolver } from "@app/pages/clubs/resolvers/course-detail.resolver";
import { CoursesResolver } from "@app/pages/clubs/resolvers/courses.resolver";
import { MeetingDetailResolver } from "@app/pages/clubs/resolvers/meeting-detail.resolver";
import { MeetingListResolver } from "@app/pages/clubs/resolvers/meeting-list.resolver";
import { PollDetailResolver } from "@app/pages/clubs/resolvers/poll-detail.resolver";
import { PollListResolver } from "@app/pages/clubs/resolvers/poll-list.resolver";
import { postGuard } from "../../../src/guards/deactive-guards/post.guard";
import { reviewGuard } from "../../../src/guards/deactive-guards/review.guard";
import { booklistGuard } from "../../../src/guards/deactive-guards/booklist.guard";
import { quoteGuard } from "../../../src/guards/deactive-guards/quote.guard";
import { progressGuard } from "../../../src/guards/deactive-guards/progress.guard";
import { DonationRoutes } from "./pre-donation/routes/donation.routes";
import { profileResolver } from "./profile/resolvers/profile.resolver";
import { mergeBookGuard } from "@shared/guard/merge-book.guard";

export const LayoutRouting: Routes = [
  // ? Feeds
  {
    path: BehkhaanRoutes.Feeds,
    canActivate: [authGuard],
    loadChildren: () => import("@app/pages/feeds/feeds.routing").then(r => r.FeedsRouting),
  },
  // ? Explore
  {
    path: BehkhaanRoutes.Explore,
    loadComponent: () =>
      import("@app/pages/explore/explore.component").then(
        (component) => component.ExploreComponent
      ),
  },
  // ? Books Routes
  {
    path: BehkhaanRoutes.Book.Books,
    children: [
      // ? Create and Edit book
      {
        path: BehkhaanRoutes.Book.Create,
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            "@app/pages/books/pages/create-book/create-book.component"
          ).then((component) => component.CreateBookComponent),
      },
      // ? History
      {
        path: BehkhaanRoutes.Book.History + '/:bookId',
        canDeactivate: [progressGuard],
        loadComponent: () => import("@app/pages/books/pages/history/history.component").then((component) => component.HistoryComponent),
      },
      // ? Edit Book (must detele)
      {
        path: BehkhaanRoutes.Book.Edit,
        canActivate: [authGuard],
        loadComponent: () => import("@app/pages/books/pages/update-book/update-book.component").then((c) => c.UpdateBookComponent),
      },
      // ? Other edition
      {
        path: BehkhaanRoutes.Book.OtherEditions + "/:id",
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            "@app/pages/books/pages/book-other-editions/book-other-editions.component"
          ).then((component) => component.BookOtherEditionsComponent),
      },
      // ? Combine Books -- هم نسخه سازی
      {
        path: BehkhaanRoutes.Book.Combine + '/:authorId',
        canActivate: [authGuard],
        loadComponent: () => import("@app/pages/books/pages/combine-books/combine-books.component").then((c) => c.CombineBooksComponent),
      },
      // ? Add book edition -- نسخه ی جدید
      {
        path: BehkhaanRoutes.Book.AddEdition + '/:bookId',
        canActivate: [authGuard],
        loadComponent: () => import("@app/pages/books/pages/create-book-edition/create-book-edition.component").then((c) => c.CreateBookEditionComponent),
      },
      // ? Book Clubs page
      {
        path: BehkhaanRoutes.Book.Clubs + "/:id",
        loadComponent: () =>
          import("@app/pages/books/book-clubs/book-clubs.component").then(
            (component) => component.BookClubsComponent
          ),
      },
      // ? Book Detail route enable with bookId
      {
        path: ":id",
        data: {
          shouldReuse: false,
          key: "book_detail",
        },
        resolve: {
          bookData: BookDetailResolver,
        },
        loadComponent: () =>
          import(
            "@app/pages/books/pages/book-detail/book-detail.component"
          ).then((component) => component.BookDetailComponent),
      },
    ],
  },
  // ? search
  {
    path: "search",
    loadChildren: () => import("@app/pages/search/search-result/search-result.routing").then(r => r.routes),
  },
  // ? Booklist Routes
  {
    path: BehkhaanRoutes.BookList.base,
    children: [
      {
        path: 'create',
        canDeactivate: [booklistGuard],
        loadComponent: () => import("@app/pages/booklists/pages/create-booklist/create-booklist.component").then((component) => component.CreateBooklistComponent)
      },
      {
        path: "comment/:id",
        loadComponent: () =>
          import(
            "@app/pages/booklists/pages/comment-book-list/comment-book-list.component"
          ).then((component) => component.CommentBookListComponent),
      },
      {
        path: ":id",
        loadComponent: () =>
          import(
            "@app/pages/booklists/pages/detail-booklist/detail-booklist.component"
          ).then((component) => component.DetailBooklistComponent),
        resolve: {
          booklist: BooklistDetailResolver,
        },
      },
    ],
  },
  // ? All Booklists
  {
    path: BehkhaanRoutes.BookList.booklists,
    loadComponent: () =>
      import("@app/pages/booklists/booklists.component").then(
        (component) => component.BooklistsComponent
      ),
  },
  // ? Profile Routes
  {
    path: BehkhaanRoutes.Profile.base,
    children: [
      // ? Settings
      {
        path: BehkhaanRoutes.Profile.Settings.base,
        canActivate: [authGuard],
        children: [
          {
            path: "",
            loadComponent: () =>
              import(
                "@app/pages/profile/pages/profile-settings/profile-settings.component"
              ).then((component) => component.ProfileSettingsComponent),
          },
          {
            path: "changepassword",
            loadComponent: () =>
              import(
                "@app/pages/profile/pages/set-password/set-password.component"
              ).then((component) => component.SetPasswordComponent),
          },
          {
            path: BehkhaanRoutes.Profile.Settings.Access,
            loadComponent: () =>
              import(
                "@app/pages/profile/pages/user-access-level/user-access-level.component"
              ).then((component) => component.UserAccessLevelComponent),
          },
          {
            path: BehkhaanRoutes.Profile.Settings.Restrict,
            loadComponent: () =>
              import(
                "@app/pages/profile/pages/restric-users/restricted-users-list.component"
              ).then((component) => component.RestricUsersComponent),
          },
          {
            path: "migrate",
            loadChildren: () =>
              import("@app/pages/profile/pages/migrate/migrate.routing").then(
                (r) => r.migrateRoutes
              ),
          },
          {
            path: "friends",
            loadChildren: () =>
              import("@app/pages/profile/pages/friends/friends.routing").then(
                (r) => r.friendsRoutes
              ),
          },
          {
            path: "feedsFilter",
            loadComponent: () =>
              import(
                "@app/pages/profile/pages/feeds-filter/feeds-filter.component"
              ).then((feedsFilter) => feedsFilter.FeedsFilterComponent),
          },
        ],
      },
      // ? Author Books
      {
        path: BehkhaanRoutes.Profile.Books + "/:authorId",
        loadComponent: () =>
          import(
            "@app/pages/books/pages/author-books/author-books.component"
          ).then((component) => component.AuthorBooksComponent),
        data: {
          author: true,
          shouldReuse: true,
          key: "AuthorBooks",
        },
      },
      // ? Edit
      {
        path: BehkhaanRoutes.Profile.Edit,
        canActivate: [authGuard],
        loadComponent: () => import('@app/pages/profile/pages/edit-profile/edit-profile.component').then(component => component.EditProfileComponent)
      },
      {
        path: BehkhaanRoutes.Profile.Bookmark,
        canActivate: [authGuard],
        loadComponent: () => import('@app/pages/profile/pages/bookmark/bookmark.component').then(component => component.BookmarkComponent)
      },
      // ? Profile
      {
        path: BehkhaanRoutes.Profile.Detail,
        // data: {
        //   preload: true,
        //   loadAfterSeconds: 10,
        //   shouldReuse: true,
        //   key: "Profile",
        // },
        resolve: {
          data: profileResolver
        },
        loadComponent: () =>
          import("@app/pages/profile/profile.component").then(
            (component) => component.ProfileComponent
          ),
      },
    ],
  },
  // ?pre Donation

  {
    path: BehkhaanRoutes.Donation.base,
    children: DonationRoutes,
  },


  // ? Library
  {
    path: BehkhaanRoutes.Library.base,
    children: [
      {
        path: BehkhaanRoutes.Library.Books + "/:id",
        loadComponent: () =>
          import(
            "@app/pages/library/pages/library-books/library-books.component"
          ).then((component) => component.LibraryBooksComponent),
      },
      {
        path: BehkhaanRoutes.Library.Detail,
        loadComponent: () =>
          import("@app/pages/library/library.component").then(
            (component) => component.LibraryComponent
          ),
        data: {
          key: "Library",
          shouldReuse: true,
        },
      },
    ],
  },
  // ? Author Request
  {
    path: BehkhaanRoutes.authorRequest + "/:id",
    canActivate: [authGuard],
    loadComponent: () =>
      import("@app/pages/author-request/author-request.component").then(
        (Author) => Author.AuthorRequestComponent
      ),
  },
  // ? Author Merg
  {
    path: BehkhaanRoutes.merge,
    canActivate: [authGuard, mergeBookGuard],
    title: "بهخوان | تلفیق نویسنده",
    loadComponent: () =>
      import("@app/pages/merge/merge.component").then(
        (mergePage) => mergePage.MergeComponent
      ),
  },
  // * Edited New Routes
  // ? Review Route(s)
  {
    path: BehkhaanRoutes.Review.Reviews,
    children: [
      {
        path: 'create',
        canDeactivate: [reviewGuard],
        loadComponent: () => import("@app/pages/reviews/pages/creat-review/creat-review.component").then((components) => components.CreatReviewComponent),
      },
      {
        path: ":id",
        loadComponent: () =>
          import(
            "@app/pages/reviews/pages/detail-review/detail-review.component"
          ).then((components) => components.DetailReviewComponent),
        resolve: {
          review: ReviewDetailResolver,
        },
      },
      {
        path: "",
        loadComponent: () =>
          import("@app/pages/reviews/reviews.component").then(
            (components) => components.ReviewsComponent
          ),
      },
    ],
  },
  // ? Publisher Route(s)
  {
    path: BehkhaanRoutes.Publisher.base,
    children: [
      {
        path: BehkhaanRoutes.Publisher.Edit + '/:id',
        canActivate: [authGuard],
        loadComponent: () => import("@app/pages/publisher/pages/edit-publisher/edit-publisher.component").then((c) => c.EditPublisherComponent),
      },
      {
        path: BehkhaanRoutes.Publisher.Authors + "/:id",
        loadComponent: () =>
          import(
            "@app/pages/publisher/pages/publisher-authors/publisher-authors.component"
          ).then((c) => c.PublisherAuthorsComponent),
      },
      {
        path: BehkhaanRoutes.Publisher.Books + "/:id",
        loadComponent: () =>
          import(
            "@app/pages/publisher/pages/publisher-books/publisher-books.component"
          ).then((component) => component.PublisherBooksComponent),
      },
      {
        path: BehkhaanRoutes.Publisher.request + "/:id",
        canActivate: [authGuard],
        loadComponent: () =>
          import("@app/pages/publisher/pages/publisher-request/publisher-request.component").then(
            (publisher) => publisher.PublisherRequestComponent
          ),
      },
    ],
  },
  // ? Quote Route(s)
  /**
   * T: all ==> For all quotes
   * T: user, id: userId ==> for all user quotes
   * T: publisher, id: publisherId ==> for all publisher quotes
   * T: book , id: bookId ==> for all books quotes
   */
  {
    path: BehkhaanRoutes.Quote.quotes,
    loadComponent: () =>
      import("@app/pages/quotes/quotes.component").then(
        (component) => component.QuotesComponent
      ),
  },
  // ? Create and Detail Quote
  {
    path: BehkhaanRoutes.Quote.base,
    children: [
      {
        path: BehkhaanRoutes.Quote.create,
        canDeactivate: [quoteGuard],
        loadComponent: () => import('@app/pages/quotes/pages/create-quote/create-quote.component').then((component) => component.CreateQuoteComponent)
      },
      {
        path: ":id",
        loadComponent: () =>
          import(
            "@app/pages/quotes/pages/detail-quote/detail-quote.component"
          ).then((component) => component.DetailQuoteComponent),
        resolve: {
          quote: QuoteDetailResolver,
        },
      },
    ],
  },
  // ? Post Routes
  /**  Query params
   * T: all ==> For all posts
   * T: user, id: userId ==> for all user posts
   * T: publisher, id: publisherId ==> for all publisher posts
   * T: book , id: bookId ==> for all books posts
   */
  {
    path: BehkhaanRoutes.Post.Posts,
    loadComponent: () =>
      import("@app/pages/posts/posts.component").then(
        (posts) => posts.PostsComponent
      ),
  },
  // ? Create and Detail Post
  {
    path: BehkhaanRoutes.Post.base,
    children: [
      {
        path: BehkhaanRoutes.Post.Create,
        canActivate: [BloggerGuard],
        canDeactivate: [postGuard],
        loadComponent: () => import('@app/pages/posts/pages/create-post/create-post.component').then((component) => component.CreatePostComponent)
      },
      {
        path: BehkhaanRoutes.Post.Detail,
        data: {
          breadcrumb: "Post",
        },
        resolve: {
          post: PostDetailResolver,
        },
        loadComponent: () =>
          import(
            "@app/pages/posts/pages/detail-post/detail-post.component"
          ).then((component) => component.DetailPostComponent),
      },
    ],
  },
  // ? Challenge Link
  {
    path: BehkhaanRoutes.Challenge.base,
    loadChildren: () =>
      import("@app/pages/challenges/challenges.routing").then(
        (module) => module.ChallengesRouting
      ),
  },
  // ? Notification
  {
    path: BehkhaanRoutes.Notifications,
    canActivate: [authGuard],
    loadComponent: () => import("@app/pages/notifications/notification-page.component").then(component => component.NotificationPageComponent)
  },
  // ? About Us
  {
    path: BehkhaanRoutes.About,
    data: {
      shouldReuse: true,
      key: "About_Us",
    },
    title: "بهخوان | دربارۀ ما",
    loadComponent: () =>
      import("@app/pages/about-us/about-us.component").then(
        (component) => component.AboutUsComponent
      ),
  },
  // ? Genre Routes
  {
    path: BehkhaanRoutes.Genre.base,
    children: [
      {
        path: ":id",
        loadComponent: () =>
          import("@app/pages/genres/genre-books/genre-books.component").then(
            (component) => component.GenreBooksComponent
          ),
      },
      {
        path: "",
        loadComponent: () =>
          import("@app/pages/genres/genres.component").then(
            (component) => component.GenresComponent
          ),
      },
    ],
  },
  // ? Promotion
  {
    path: BehkhaanRoutes.Promotion + "/:id",
    loadComponent: () =>
      import("@app/pages/promotion/promotion.component").then(
        (component) => component.PromotionComponent
      ),
  },
  // ? States
  {
    path: BehkhaanRoutes.states + "/:id",
    loadComponent: () =>
      import("@app/pages/books/pages/states/states.component").then(
        (component) => component.StatesComponent
      ),
  },
  // ? Club
  {
    path: BehkhaanRoutes.Club.base,
    children: [
      {
        path: BehkhaanRoutes.Club.list + '/:id',
        canActivate: [authGuard],
        loadComponent: () => import('@app/pages/clubs/clubs.component').then(component => component.ClubsComponent),
        resolve: {
          clubs: ClubsResolver,
        },
      },
      {
        path: BehkhaanRoutes.Club.create,
        canActivate: [authGuard, ClubOwnerGuard],
        loadComponent: () => import('@app/pages/clubs/pages/club-create/club-create.component').then(component => component.ClubCreateComponent)
      },
      {
        path: BehkhaanRoutes.Club.edit + '/:id',
        canActivate: [authGuard, clubAdminGuard],
        loadComponent: () => import('@app/pages/clubs/pages/club-edit/club-edit.component').then(component => component.ClubEditComponent)
      },
      {
        path: BehkhaanRoutes.Club.intro + "/:id",
        loadComponent: () =>
          import("@app/pages/clubs/pages/club-intro/club-intro.component").then(
            (component) => component.ClubIntroComponent
          ),
        resolve: {
          clubPreview: ClubIntroResolver,
        },
      },
      {
        path: BehkhaanRoutes.Club.members + '/:id',
        canActivate: [authGuard, clubMemberGuard],
        loadComponent: () => import('@app/pages/clubs/pages/club-members/club-members.component').then(component => component.ClubMembersComponent),
        // resolve: {
        //   clubMembers: ClubMembersResolver,
        // },
      },
      {
        path: ":id",
        loadComponent: () =>
          import(
            "@app/pages/clubs/pages/club-detail/club-detail.component"
          ).then((component) => component.ClubDetailComponent),
        resolve: {
          clubDetail: ClubDetailResolver,
        },
      },
    ],
  },
  // ? Course
  {
    path: BehkhaanRoutes.Course.base,
    children: [
      {
        path: BehkhaanRoutes.Course.create,
        canActivate: [clubAdminGuard],
        loadComponent: () => import('@app/pages/clubs/pages/course-create/course-create.component').then(component => component.CourseCreateComponent)
      },
      {
        path: BehkhaanRoutes.Course.all + '/:id',
        canActivate: [clubMemberGuard],
        loadComponent: () => import('@app/pages/clubs/pages/courses/courses.component').then(component => component.CoursesComponent),
        resolve: {
          CoursesResolver: CoursesResolver,
        },
      },
      {
        path: ':id',
        canActivate: [clubMemberGuard],
        loadComponent: () => import('@app/pages/clubs/pages/course-details/course-details.component').then(component => component.CourseDetailsComponent),
        resolve: {
          CourseDetailResolver: CourseDetailResolver,
        },
      },
    ],
  },
  // ? Meeting
  {
    path: BehkhaanRoutes.Meeting.base,
    children: [
      {
        path: BehkhaanRoutes.Meeting.create,
        canActivate: [clubAdminGuard],
        loadComponent: () => import('@app/pages/clubs/pages/meeting-create/meeting-create.component').then(component => component.MeetingCreateComponent)
      },
      {
        path: BehkhaanRoutes.Meeting.all + '/:id',
        canActivate: [clubMemberGuard],
        loadComponent: () => import('@app/pages/clubs/pages/meeting-list/meeting-list.component').then(component => component.MeetingListComponent),
        resolve: {
          MeetingListResolver: MeetingListResolver,
        },
      },
      {
        path: ':id',
        canActivate: [clubMemberGuard],
        loadComponent: () => import('@app/pages/clubs/pages/meeting-detail/meeting-detail.component').then(component => component.MeetingDetailComponent),
        resolve: {
          MeetingDetailResolver: MeetingDetailResolver,
        },
      },
    ],
  },
  // ? Poll
  {
    path: BehkhaanRoutes.Poll.base,
    children: [
      {
        path: BehkhaanRoutes.Poll.create,
        canActivate: [clubAdminGuard],
        loadComponent: () => import('@app/pages/clubs/pages/poll-create/poll-create.component').then(component => component.PollCreateComponent)
      },
      {
        path: BehkhaanRoutes.Poll.all + '/:id',
        canActivate: [clubMemberGuard],
        loadComponent: () => import('@app/pages/clubs/pages/poll-list/poll-list.component').then(component => component.PollListComponent),
        resolve: {
          PollListResolver: PollListResolver,
        },
      },
      {
        path: ':id',
        canActivate: [clubMemberGuard],
        loadComponent: () => import('@app/pages/clubs/pages/poll-detail/poll-detail.component').then(component => component.PollDetailComponent),
        resolve: {
          PollDetailResolver: PollDetailResolver,
        },
      },
    ],
  },
  // ? Added Books
  {
    path: BehkhaanRoutes.addedBooks,
    canActivate: [authGuard],
    loadComponent: () => import('./books/pages/user-added-books/user-added-books.component').then(component => component.UserAddedBooksComponent)
  },
  // ? Annual Report
  {
    path: BehkhaanRoutes.Annual.base,
    children: [
      {
        path: BehkhaanRoutes.Annual.detail + "/:id",
        title: "بهخوان | جزئیات گزارش سالانه",
        loadComponent: () =>
          import("@app/pages/annual-report/annual-report-detail.component").then(
            (component) => component.AnnualReportDetailComponent
          ),
      },
    ],
  },
  // ? Policy
  {
    path: BehkhaanRoutes.POLICY,
    loadComponent: () =>
      import("@app/pages/policy/policy.component").then(
        (component) => component.PolicyComponent
      ),
  },
  // ? contact us
  {
    path: BehkhaanRoutes.CONTACT_US,
    loadChildren: () => import("@app/pages/contact-us/contact-us.routes").then(r => r.CONTACT_US_ROUTES),
  },
  // ? category books
  {
    path: 'category',
    loadChildren: () => import('@app/pages/category-books-container/category-books.routing').then(r => r.CATEGORY_BOOKS_ROUTES)
  },
  // ? follow requests
  {
    path: BehkhaanRoutes.FOLLOW_REQUESTS,
    loadChildren: () => import('@app/pages/follow-request-list-container/category-books.routing').then(r => r.FOLLOW_REQUEST_LIST__CONTAINER_ROUTES)
  },
  // ? annual report
  {
    path: BehkhaanRoutes.ANNUAL_REPORT,
    loadChildren: () => import('@app/pages/annual-report-container/annual-report.routes').then(r => r.ANNUAL_REPORT_ROUTES)
  },
  // ? behkhaan guid
  {
    path: BehkhaanRoutes.BEHKHAAN_GUID,
    loadComponent: () => import('@app/pages/behkhaan-guid/behkhaan-guid.component').then(r => r.BehkhaanGuidComponent)
  }
];
